// src/layout/builders/Payment/PaymentRequest.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  PlusCircle,
  Send,
  MessageSquare,
  Edit3,
  Clock,
  IndianRupee,
  HelpCircle,
  X as CloseIcon,
  CheckCircle,
  XCircle,
} from "lucide-react";
import ConfirmationPopup from "../../../components/common/ConfirmationPopup";
import GenericModal from "../../../components/common/GenericModal"; // Import GenericModal
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import usePaymentStore from "../../../store/builders/usePaymentStore";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const formatDisplayDateTime = (isoDateString) => {
  if (!isoDateString) return "N/A";
  try {
    return new Date(isoDateString)
      .toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", "");
  } catch (e) {
    return "Invalid Date";
  }
};

const requestStatusDisplayInfo = (status) => {
  switch (status) {
    case "Approved by Customer":
      return {
        icon: <CheckCircle className="w-3.5 h-3.5 text-green-500" />,
        text: "Paid",
        bgClass: "bg-green-100 text-green-700",
      };
    case "Pending Customer Action":
      return {
        icon: <Clock className="w-3.5 h-3.5 text-yellow-500" />,
        text: "Pending Customer Action",
        bgClass: "bg-yellow-100 text-yellow-700",
      };
    case "Rejected by Customer":
      return {
        icon: <XCircle className="w-3.5 h-3.5 text-red-500" />,
        text: "Rejected by Customer",
        bgClass: "bg-red-100 text-red-700",
      };
    default:
      return {
        icon: <HelpCircle className="w-3.5 h-3.5 text-gray-500" />,
        text: status || "Unknown",
        bgClass: "bg-gray-100 text-gray-700",
      };
  }
};

const paymentStatusDisplayInfo = (status) => {
  switch (status) {
    case "Paid":
      return {
        icon: <CheckCircle className="w-3.5 h-3.5 text-green-600" />,
        text: "Paid",
        textClass: "text-green-600",
      };
    case "Payment Pending":
      return {
        icon: <Clock className="w-3.5 h-3.5 text-orange-500" />,
        text: "Payment Pending",
        textClass: "text-orange-600",
      };
    case "Not Applicable":
      return {
        icon: <HelpCircle className="w-3.5 h-3.5 text-gray-400" />,
        text: "Not Applicable",
        textClass: "text-gray-500",
      };
    default:
      return {
        icon: <HelpCircle className="w-3.5 h-3.5 text-gray-400" />,
        text: "N/A",
        textClass: "text-gray-500",
      };
  }
};

// --- Payment Methods for Dropdown ---
const PAYMENT_METHODS = [
  "Direct Bank Transfer (NEFT/RTGS/IMPS)",
  "Cash",
  "Cheque",
  "UPI (Google Pay, PhonePe, Paytm, etc.)",
  "Other",
];

const PaymentRequest = ({ projectId }) => {
  const {
    builderPaymentRequests,
    isPaymentRequestsLoading,
    isRequestSubmitting,
    isConfirmingPayment,
    fetchBuilderPaymentRequests,
    createBuilderPaymentRequest,
    confirmPaymentReceived,
    // --- MODIFICATION START ---
    projectValueEntries,
    fetchProjectValueEntries,
    // --- MODIFICATION END ---
  } = usePaymentStore();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newRequestData, setNewRequestData] = useState({
    amount: "",
    comment: "",
  });
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [currentRejectionReason, setCurrentRejectionReason] = useState("");

  const [isConfirmPaymentModalOpen, setIsConfirmPaymentModalOpen] =
    useState(false);
  const [currentRequestForPaymentConfirm, setCurrentRequestForPaymentConfirm] =
    useState(null);
  const [paymentConfirmationData, setPaymentConfirmationData] = useState({
    paymentMethod: PAYMENT_METHODS[0],
    transactionReferenceId: "",
    comment: "",
  });

  const [confirmPopup, setConfirmPopup] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => { },
    isLoading: false,
  });

  useEffect(() => {
    if (projectId) {
      fetchBuilderPaymentRequests(projectId);
      // --- MODIFICATION START ---
      fetchProjectValueEntries(projectId);
      // --- MODIFICATION END ---
    }
  }, [projectId, fetchBuilderPaymentRequests, fetchProjectValueEntries]);

  // --- MODIFICATION START: Calculate latest approved project value and total requested amount ---
  const latestProjectValue = useMemo(() => {
    const latestApprovedEntry = projectValueEntries.find(
      (entry) => entry.status === "Approved"
    );
    return latestApprovedEntry
      ? parseFloat(
        latestApprovedEntry.amount?.toString().replace(/,/g, "") || "0"
      )
      : 0;
  }, [projectValueEntries]);

  const totalAlreadyRequestedAmount = useMemo(() => {
    return (builderPaymentRequests || [])
      .filter((req) => req.status !== "Rejected by Customer")
      .reduce((sum, req) => sum + parseFloat(req.amount), 0);
  }, [builderPaymentRequests]);
  // --- MODIFICATION END ---

  const handleOpenCreateModal = () => {
    setNewRequestData({ amount: "", comment: "" });
    setIsCreateModalOpen(true);
  };
  const handleCloseCreateModal = () => setIsCreateModalOpen(false);

  const handleCreateRequestSubmit = async () => {
    const requestedAmount = parseFloat(newRequestData.amount.replace(/,/g, ""));

    if (
      !newRequestData.amount.trim() ||
      isNaN(requestedAmount) ||
      requestedAmount <= 0
    ) {
      toast.error("Please enter a valid positive amount.");
      return;
    }
    if (!newRequestData.comment.trim()) {
      toast.error("Please provide a comment or description for the request.");
      return;
    }

    // --- MODIFICATION START: VALIDATION LOGIC ---
    if (
      latestProjectValue > 0 &&
      totalAlreadyRequestedAmount + requestedAmount > latestProjectValue
    ) {
      toast.error(
        <div>
          <p className="font-semibold">Request Exceeds Project Value!</p>
          <p className="text-sm mt-1">
            Total requests cannot exceed the latest approved project value of{" "}
            <b>₹{latestProjectValue.toLocaleString("en-IN")}</b>.
          </p>
          <p className="text-xs mt-2 text-gray-500">
            Current Requested/Paid: ₹
            {totalAlreadyRequestedAmount.toLocaleString("en-IN")}
          </p>
        </div>,
        { duration: 6000 }
      );
      return;
    }
    // --- MODIFICATION END ---

    setConfirmPopup({
      isOpen: true,
      title: "Confirm Payment Request",
      message: `Create a payment request for ₹${requestedAmount.toLocaleString(
        "en-IN"
      )}? This will be sent to the customer for approval.`,
      onConfirm: async () => {
        setConfirmPopup((prev) => ({ ...prev, isLoading: true }));
        const success = await createBuilderPaymentRequest(
          projectId,
          newRequestData
        );
        if (success) {
          handleCloseCreateModal();
        }
        setConfirmPopup({ isOpen: false, isLoading: false });
      },
      isLoading: isRequestSubmitting,
    });
  };

  const handleViewReason = (reason) => {
    setCurrentRejectionReason(reason);
    setIsReasonModalOpen(true);
  };
  const handleCloseReasonModal = () => {
    setIsReasonModalOpen(false);
    setCurrentRejectionReason("");
  };

  const handleOpenConfirmPaymentModal = (request) => {
    setCurrentRequestForPaymentConfirm(request);
    setPaymentConfirmationData({
      paymentMethod: PAYMENT_METHODS[0],
      transactionReferenceId: "",
      comment: "",
    });
    setIsConfirmPaymentModalOpen(true);
  };

  const handleCloseConfirmPaymentModal = () => {
    setIsConfirmPaymentModalOpen(false);
    setCurrentRequestForPaymentConfirm(null);
  };

  const handlePaymentConfirmationChange = (e) => {
    const { name, value } = e.target;
    setPaymentConfirmationData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirmPaymentSubmit = async () => {
    if (!paymentConfirmationData.paymentMethod) {
      toast.error("Please select a payment method.");
      return;
    }

    const result = await confirmPaymentReceived(
      projectId,
      currentRequestForPaymentConfirm.id,
      paymentConfirmationData
    );
    if (result.success) {
      handleCloseConfirmPaymentModal();
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
        <h3 className="text-sm sm:text-md font-semibold text-gray-700 ml-1">
          Customer Payment Requests
        </h3>
        <button
          onClick={handleOpenCreateModal}
          disabled={isRequestSubmitting || confirmPopup.isLoading}
          className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-colors duration-200 shadow-sm hover:shadow-md disabled:opacity-60"
        >
          {isRequestSubmitting || confirmPopup.isLoading ? (
            <AiOutlineLoading3Quarters className="animate-spin" />
          ) : (
            <PlusCircle size={16} />
          )}
          Create New Request
        </button>
      </div>

      {isPaymentRequestsLoading ? (
        <div className="flex justify-center items-center py-10">
          <AiOutlineLoading3Quarters className="animate-spin text-2xl text-blue-500" />
          <p className="ml-2 text-xs sm:text-sm text-gray-500">Loading payment requests...</p>
        </div>
      ) : builderPaymentRequests.length === 0 ? (
        <div className="text-center py-6 sm:py-8 text-gray-500 bg-white rounded-lg shadow p-4 sm:p-6">
          <PlusCircle className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-gray-300" />
          <p className="text-base sm:text-lg font-semibold">No Payment Requests Sent Yet</p>
          <p className="text-xs sm:text-sm mt-1">
            Click "Create New Request" to send a payment request to the
            customer.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table View - Hidden on mobile */}
          <div className="hidden md:block rounded-lg overflow-hidden border border-gray-200">
            {/* Heading Row */}
            <div className="grid grid-cols-[180px_1fr_160px_200px] items-center bg-gray-50 px-6 py-3 font-semibold text-xs text-gray-700 border-b border-gray-200">
              <span>Date & Time</span>
              <span>Comment</span>
              <span className="justify-end flex">Amount</span>
              <span className="justify-end flex">Status / Action</span>
            </div>
            {/* Data Rows */}
            {builderPaymentRequests.map((request, index) => {
              const reqStatusInfo = requestStatusDisplayInfo(request.status);
              const canConfirmPayment =
                request.status === "Approved by Customer" &&
                request.payment_status === "Payment Pending";

              return (
                <div
                  key={request.id || index}
                  className="grid grid-cols-[180px_1fr_160px_200px] items-center bg-white px-6 py-4 border-b border-gray-100 text-sm font-medium"
                >
                  {/* Date/Time */}
                  <span className="text-gray-600 flex items-center">
                    <Clock className="w-3 h-3 mr-1 text-gray-400" />
                    {formatDisplayDateTime(request.request_date)}
                  </span>
                  {/* Comment */}
                  <span
                    className="text-gray-800 truncate"
                    title={request.comment}
                  >
                    {request.comment}
                  </span>
                  {/* Amount */}
                  <span className="font-semibold text-gray-900 flex items-center justify-end text-base">
                    <IndianRupee className="w-4 h-4 mr-1 text-gray-700" />
                    {parseFloat(request.amount).toLocaleString("en-IN")}
                  </span>
                  {/* Status / Action */}
                  <div className="flex justify-end items-center">
                    {canConfirmPayment ? (
                      <button
                        onClick={() => handleOpenConfirmPaymentModal(request)}
                        disabled={isConfirmingPayment}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors duration-200 shadow-sm hover:shadow-md disabled:opacity-70"
                      >
                        {isConfirmingPayment &&
                          currentRequestForPaymentConfirm?.id === request.id ? (
                          <AiOutlineLoading3Quarters className="animate-spin w-3.5 h-3.5" />
                        ) : (
                          <CheckCircle size={14} />
                        )}
                        Confirm Payment
                      </button>
                    ) : (
                      <span
                        className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${reqStatusInfo.bgClass}`}
                      >
                        {reqStatusInfo.icon}
                        <span className="ml-1.5">{reqStatusInfo.text}</span>
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile Card View - Shown only on mobile */}
          <div className="md:hidden space-y-3">
            {builderPaymentRequests.map((request, index) => {
              const reqStatusInfo = requestStatusDisplayInfo(request.status);
              const canConfirmPayment =
                request.status === "Approved by Customer" &&
                request.payment_status === "Payment Pending";

              return (
                <div
                  key={request.id || index}
                  className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm"
                >
                  {/* Date & Amount Row */}
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center text-xs text-gray-600">
                      <Clock className="w-3 h-3 mr-1 text-gray-400" />
                      {formatDisplayDateTime(request.request_date)}
                    </div>
                    <div className="font-semibold text-gray-900 flex items-center text-sm">
                      <IndianRupee className="w-3.5 h-3.5 mr-0.5 text-gray-700" />
                      {parseFloat(request.amount).toLocaleString("en-IN")}
                    </div>
                  </div>

                  {/* Comment */}
                  <div className="mb-2">
                    <p className="text-xs text-gray-500 mb-1">Comment:</p>
                    <p className="text-xs text-gray-800">{request.comment}</p>
                  </div>

                  {/* Status / Action */}
                  <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                    {canConfirmPayment ? (
                      <button
                        onClick={() => handleOpenConfirmPaymentModal(request)}
                        disabled={isConfirmingPayment}
                        className="w-full bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors duration-200 shadow-sm disabled:opacity-70"
                      >
                        {isConfirmingPayment &&
                          currentRequestForPaymentConfirm?.id === request.id ? (
                          <AiOutlineLoading3Quarters className="animate-spin w-3.5 h-3.5" />
                        ) : (
                          <CheckCircle size={14} />
                        )}
                        Confirm Payment
                      </button>
                    ) : (
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${reqStatusInfo.bgClass} w-full justify-center`}
                      >
                        {reqStatusInfo.icon}
                        <span className="ml-1.5">{reqStatusInfo.text}</span>
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Create Request Modal */}
      <AnimatePresence>
        {isCreateModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] p-4"
            onClick={handleCloseCreateModal}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <PlusCircle size={20} className="text-blue-500" /> Create New
                  Payment Request
                </h3>
                <button
                  onClick={handleCloseCreateModal}
                  disabled={isRequestSubmitting || confirmPopup.isLoading}
                  className="text-gray-400 hover:text-gray-600 p-1 disabled:opacity-50"
                >
                  <CloseIcon size={20} />
                </button>
              </div>
              <div className="mb-4">
                <label
                  htmlFor="requestAmount"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Amount (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="requestAmount"
                  value={newRequestData.amount}
                  onChange={(e) =>
                    setNewRequestData({
                      ...newRequestData,
                      amount: e.target.value.replace(/[^0-9.]/g, ""),
                    })
                  }
                  placeholder="Enter amount"
                  className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  disabled={isRequestSubmitting || confirmPopup.isLoading}
                />
              </div>
              <div className="mb-5">
                <label
                  htmlFor="requestComment"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Comment / Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="requestComment"
                  value={newRequestData.comment}
                  onChange={(e) =>
                    setNewRequestData({
                      ...newRequestData,
                      comment: e.target.value,
                    })
                  }
                  placeholder="Enter a brief description for this payment request..."
                  rows={3}
                  className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  disabled={isRequestSubmitting || confirmPopup.isLoading}
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCloseCreateModal}
                  disabled={isRequestSubmitting || confirmPopup.isLoading}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-xs font-medium transition-colors disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRequestSubmit}
                  disabled={
                    !newRequestData.amount.trim() ||
                    !newRequestData.comment.trim() ||
                    isNaN(parseFloat(newRequestData.amount)) ||
                    confirmPopup.isLoading ||
                    isRequestSubmitting
                  }
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
                >
                  {confirmPopup.isLoading || isRequestSubmitting ? (
                    <AiOutlineLoading3Quarters className="animate-spin h-4 w-4" />
                  ) : (
                    <Send size={14} />
                  )}
                  Create & Send
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rejection Reason Modal */}
      <AnimatePresence>
        {isReasonModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4"
            onClick={handleCloseReasonModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <MessageSquare size={18} className="text-red-500" />{" "}
                  Customer's Rejection Reason
                </h3>
                <button
                  onClick={handleCloseReasonModal}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <CloseIcon size={18} />
                </button>
              </div>
              <div className="bg-red-50 border border-red-200 p-3 rounded-md">
                <p className="text-sm text-red-700 whitespace-pre-wrap">
                  {currentRejectionReason ||
                    "No specific reason provided by customer."}
                </p>
              </div>
              <div className="mt-4 text-right">
                <button
                  onClick={handleCloseReasonModal}
                  className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs font-medium"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmationPopup
        isOpen={confirmPopup.isOpen}
        onClose={() => setConfirmPopup({ isOpen: false, isLoading: false })}
        onConfirm={confirmPopup.onConfirm}
        title={confirmPopup.title}
        message={confirmPopup.message}
        isLoading={confirmPopup.isLoading}
        confirmText="Yes, Proceed"
      />

      {/* Modal for Confirming Payment Received */}
      <GenericModal
        isOpen={isConfirmPaymentModalOpen}
        onClose={handleCloseConfirmPaymentModal}
        title={`Confirm Payment Received for ₹${parseFloat(
          currentRequestForPaymentConfirm?.amount || 0
        ).toLocaleString("en-IN")}`}
        size="max-w-lg"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleConfirmPaymentSubmit();
          }}
        >
          <p className="text-xs text-gray-600 mb-4">
            Request: "{currentRequestForPaymentConfirm?.comment}"
          </p>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="paymentMethod"
                className="block text-xs font-medium text-gray-700 mb-1"
              >
                Payment Method <span className="text-red-500">*</span>
              </label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={paymentConfirmationData.paymentMethod}
                onChange={handlePaymentConfirmationChange}
                className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                disabled={isConfirmingPayment}
              >
                {PAYMENT_METHODS.map((method) => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="transactionReferenceId"
                className="block text-xs font-medium text-gray-700 mb-1"
              >
                Transaction Reference ID (Optional)
              </label>
              <input
                type="text"
                id="transactionReferenceId"
                name="transactionReferenceId"
                value={paymentConfirmationData.transactionReferenceId}
                onChange={handlePaymentConfirmationChange}
                placeholder="e.g., UPI Ref, Cheque No., NEFT UTR"
                className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                disabled={isConfirmingPayment}
              />
            </div>
            <div>
              <label
                htmlFor="comment"
                className="block text-xs font-medium text-gray-700 mb-1"
              >
                Confirmation Comment (Optional)
              </label>
              <textarea
                id="comment"
                name="comment"
                value={paymentConfirmationData.comment}
                onChange={handlePaymentConfirmationChange}
                placeholder="e.g., Received full amount via NEFT."
                rows={3}
                className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                disabled={isConfirmingPayment}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleCloseConfirmPaymentModal}
              disabled={isConfirmingPayment}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-xs font-medium transition-colors disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isConfirmingPayment || !paymentConfirmationData.paymentMethod
              }
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
            >
              {isConfirmingPayment ? (
                <AiOutlineLoading3Quarters className="animate-spin h-4 w-4" />
              ) : (
                <CheckCircle size={14} />
              )}
              Confirm Payment
            </button>
          </div>
        </form>
      </GenericModal>
    </>
  );
};

export default PaymentRequest;
